import { CriteriaSubDimension } from '@abb-procure/data';
import { BooleanInput } from '@angular/cdk/coercion';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  booleanAttribute,
  computed,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { FormRowDirective } from '../../directives/form-row.directive';
import { NumericInputDirective } from '../../directives/numeric-input.directive';
import { DialogService } from '../../services/dialog.service';
import { DimensionDialogService } from '../../services/dimension-dialog.service';
import { conditionalColumns } from '../../utilities/table.util';
import { Button } from '../button/button.component';
import { Card } from '../card/card.component';
import { Hint } from '../hint/hint.component';
import { InputText } from '../input-text/input-text.component';
import { Table } from '../table/table.component';

@Component({
  selector: 'procure-selection-dimension',
  templateUrl: './selection-dimension.component.html',
  styleUrl: './selection-dimension.component.scss',
  imports: [
    FormRowDirective,
    InputText,
    Hint,
    Table,
    MatTable,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderCellDef,
    MatCell,
    MatRow,
    MatCellDef,
    MatColumnDef,
    MatNoDataRow,
    MatRowDef,
    MatHeaderRowDef,
    Button,
    MatTooltip,
    MatIcon,
    NumericInputDirective,
    Card,
  ],
})
export class SelectionDimension {
  readonly group = input.required<FormGroup>();
  readonly name = input.required<string | null | undefined>();

  readonly showMode = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly mergeDialog = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  protected readonly displayedColumns = computed(() =>
    conditionalColumns({
      subDimension: true,
      weight: true,
      actions: !this.showMode(),
    }),
  );

  protected readonly weightControl = computed(
    () => this.group().controls.weight as FormControl,
  );

  protected readonly subDimensionsControl = computed(
    () => this.group().controls.subDimensions as FormArray,
  );

  private readonly dimensionDialogService = inject(DimensionDialogService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly dialogService = inject(DialogService);
  private readonly changeRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  async openDimensionDialog(
    subDimension?: CriteriaSubDimension,
    index?: number,
  ): Promise<void> {
    const dialog = await this.dimensionDialogService.openDimensionDialog({
      dimension: subDimension,
    });

    dialog
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) {
          return;
        }

        if (index !== undefined) {
          this.subDimensionsControl().at(index).patchValue(result);
        } else {
          this.subDimensionsControl().push(this.formBuilder.group(result));
        }

        this.group().markAsDirty();
        this.changeRef.detectChanges();
      });
  }

  removeSubDimension(index: number): void {
    this.dialogService
      .showConfirmDialog({
        title: 'Remove Sub-Dimension',
        text: 'Are you sure you want to remove this sub-dimension?',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.subDimensionsControl().removeAt(index);
        this.changeRef.detectChanges();
      });
  }
}
